import type { EasingFunction, TransitionProfile } from '../../../model/widget-action.model';
import type { AnimateValueOptions, AnimationResult } from './action.model';

/**
 * Get the easing function for interpolation.
 */
export function getEasingFunction(easing: EasingFunction): (t: number) => number {
  switch (easing) {
    case 'linear':
      return (t) => t;
    case 'easeIn':
      return (t) => t * t * t;
    case 'easeOut':
      return (t) => 1 - Math.pow(1 - t, 3);
    case 'easeInOut':
      return (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    case 'elastic':
      return (t) => {
        const c4 = (2 * Math.PI) / 3;
        return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
      };
    case 'bounce':
      return (t) => {
        const n1 = 7.5625;
        const d1 = 2.75;
        if (t < 1 / d1) {
          return n1 * t * t;
        }
        if (t < 2 / d1) {
          return n1 * (t -= 1.5 / d1) * t + 0.75;
        }
        if (t < 2.5 / d1) {
          return n1 * (t -= 2.25 / d1) * t + 0.9375;
        }
        return n1 * (t -= 2.625 / d1) * t + 0.984375;
      };
    default:
      return (t) => t;
  }
}

/**
 * Check if a transition should be animated or applied instantly.
 */
export function shouldAnimate(transition?: TransitionProfile): boolean {
  return !!transition && transition.type !== 'none' && transition.duration > 0;
}

/**
 * Animate a single numeric value change with easing.
 */
export async function animateValue(options: AnimateValueOptions): Promise<AnimationResult> {
  const { startValue, endValue, transition, onStep, shouldAbort } = options;

  if (!shouldAnimate(transition)) {
    onStep(endValue);
    return { completed: true };
  }

  const steps = Math.max(1, Math.round(transition.duration / 16));
  const stepDuration = transition.duration / steps;
  const easingFn = getEasingFunction(transition.easing);

  for (let step = 1; step <= steps; step++) {
    if (shouldAbort?.()) {
      return { completed: false, aborted: true };
    }

    const progress = easingFn(step / steps);
    const interpolated = startValue + (endValue - startValue) * progress;

    onStep(interpolated);

    if (step < steps) {
      await delay(stepDuration);
    }
  }

  return { completed: true };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
